/**
 * @fileoverview Route-level error boundary for isolated recovery.
 * Catches errors in individual routes without crashing the entire app.
 */

import { Component, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { captureException } from '@/services/errorTracking';
import { logEventDirect } from '@/hooks/useEventLogger';
import { ChunkLoadError } from '@/lib/lazyWithRecovery';
import { ChunkErrorFallback } from '@/components/ChunkErrorFallback';

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
  fallbackDescription?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class RouteErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Route error boundary caught error:', error, errorInfo);
    captureException(error, { componentStack: errorInfo.componentStack, routeError: true });

    logEventDirect('route_crash', 'error', {
      error_type: 'route_error_boundary',
      error_message: error.message?.substring(0, 500),
      stack_trace: error.stack?.substring(0, 500) || null,
      component_stack: errorInfo.componentStack?.substring(0, 500) || null,
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleGoHome = () => {
    window.history.replaceState(null, '', '/');
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      // Show dedicated recovery UI for chunk/module load failures
      if (this.state.error instanceof ChunkLoadError) {
        return <ChunkErrorFallback />;
      }

      const { fallbackTitle = 'Something went wrong', fallbackDescription } = this.props;
      
      return (
        <div className="min-h-[60vh] flex flex-col items-center justify-center p-6 text-center">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
            <AlertTriangle className="w-8 h-8 text-destructive" />
          </div>
          
          <h2 className="text-xl font-semibold text-foreground mb-2">
            {fallbackTitle}
          </h2>
          
          <p className="text-sm text-muted-foreground mb-6 max-w-sm">
            {fallbackDescription || 'An error occurred while loading this section. Please try again.'}
          </p>
          
          {/* Show error in dev mode */}
          {import.meta.env.DEV && this.state.error && (
            <pre className="mb-6 p-3 bg-muted rounded-lg text-xs text-left overflow-auto max-w-full max-h-32">
              {this.state.error.message}
            </pre>
          )}
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={this.handleGoHome}
              className="gap-2"
            >
              <Home className="w-4 h-4" />
              Go Home
            </Button>
            
            <Button
              size="sm"
              onClick={this.handleRetry}
              className="gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default RouteErrorBoundary;
